import { request } from "./client";

export type ObservabilityThread = {
  id: string;
  agent: string;
  start_time: string;
  tokens_used: number;
  token_budget: number;
  turns: number;
  max_turns: number;
  active: boolean;
  reason_for_stop?: string | null;
};

export type BlackboardNote = {
  id?: string;
  agent?: string;
  thread?: string;
  status?: string;
  thought?: string | null;
  action?: string | null;
  observation?: unknown;
  payload?: Record<string, unknown> | null;
  created_at?: string;
  [key: string]: unknown;
};

export type AgentMessage = {
  id: string;
  sender: string;
  recipient: string;
  thread?: string | null;
  type: string;
  payload: Record<string, unknown>;
  created_at?: string;
};

export async function fetchThreads(): Promise<ObservabilityThread[]> {
  const payload = await request<{ threads: ObservabilityThread[] }>("/observability/agents/threads");
  return payload?.threads ?? [];
}

export async function fetchThreadNotes(agent: string, thread: string): Promise<BlackboardNote[]> {
  const data = await request<{ notes: BlackboardNote[] }>(
    `/observability/blackboard/${encodeURIComponent(agent)}/${encodeURIComponent(thread)}`,
  );
  return data?.notes ?? [];
}

export async function fetchBlackboardRecent(limit = 50): Promise<BlackboardNote[]> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  const data = await request<{ notes: BlackboardNote[] }>(`/observability/blackboard/recent?${params.toString()}`);
  return data?.notes ?? [];
}

export async function fetchAgentInbox(agentName: string): Promise<AgentMessage[]> {
  if (!agentName) return [];
  const data = await request<{ messages: AgentMessage[] }>(
    `/observability/agents/inbox/${encodeURIComponent(agentName)}`,
  );
  return data?.messages ?? [];
}

export type TimelineEvent = {
  timestamp: string;
  relative?: string;
  agent?: string;
  thread?: string;
  event_type?: string;
  summary?: string;
  note?: Record<string, unknown>;
};

export type MemoryGraph = {
  episodes: any[];
  clusters: any[];
  edges: any[];
};

export async function fetchGlobalTimeline(limit = 200): Promise<TimelineEvent[]> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  const data = await request<{ events: TimelineEvent[] }>(`/observability/timeline/global?${params.toString()}`);
  return data?.events ?? [];
}

export async function fetchThreadTimeline(agent: string, thread: string): Promise<TimelineEvent[]> {
  const data = await request<{ events: TimelineEvent[] }>(
    `/observability/timeline/${encodeURIComponent(agent)}/${encodeURIComponent(thread)}`,
  );
  return data?.events ?? [];
}

export async function fetchThreadNarrative(agent: string, thread: string): Promise<string> {
  const data = await request<{ narrative: string }>(
    `/observability/narrative/${encodeURIComponent(agent)}/${encodeURIComponent(thread)}`,
  );
  return data?.narrative ?? "";
}

export async function fetchMemoryGraph(): Promise<MemoryGraph> {
  return request<MemoryGraph>("/memory/graph");
}

export type ArtistSignalItem = {
  artist_id: string;
  name: string;
  signals: number;
  agents: string[];
  threads: string[];
  last_seen?: string | null;
  momentum: "up" | "steady" | "down";
};

export async function fetchArtistSignals(): Promise<ArtistSignalItem[]> {
  const data = await request<{ signals: ArtistSignalItem[] }>("/observability/agents/signals/artists");
  return data?.signals ?? [];
}
